import React, { useRef, useState } from 'react';
import { Upload, FolderOpen, Users, X } from 'lucide-react';
import { uploadDataUriToBackend } from '../../services/backendService';
import { UserAssetsModal } from './UserAssetsModal';
import { ActorsLibraryModal } from './ActorsLibraryModal';

export const MAX_REFERENCE_IMAGES = 4;

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
});

export const addReferenceImages = (current, incoming, max = MAX_REFERENCE_IMAGES) => {
    const out = [...(current || [])];
    for (const url of incoming) {
        if (url && !out.includes(url) && out.length < max) out.push(url);
    }
    return out;
};

const btn = 'px-2 py-1 text-[11px] rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 flex items-center gap-1 disabled:opacity-40 transition-colors';

export const ReferenceImageStrip = ({ images = [], onChange, testIdPrefix, disabled, max = MAX_REFERENCE_IMAGES }) => {
    const fileRef = useRef(null);
    const [picker, setPicker] = useState(null);
    const [uploading, setUploading] = useState(false);
    const full = images.length >= max;

    const handleFiles = async (e) => {
        const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/')).slice(0, max - images.length);
        e.target.value = '';
        if (!files.length) return;
        setUploading(true);
        try {
            const userEmail = sessionStorage.getItem('userEmail');
            const urls = await Promise.all(files.map(async (f) => {
                const dataUrl = await readAsDataUrl(f);
                try { return (await uploadDataUriToBackend(dataUrl, userEmail)) || dataUrl; } catch { return dataUrl; }
            }));
            onChange(addReferenceImages(images, urls, max));
        } finally { setUploading(false); }
    };

    const pick = (url) => { setPicker(null); if (url) onChange(addReferenceImages(images, [url], max)); };

    return (
        <div className="mt-3 p-2 rounded-lg bg-white/[0.02] border border-white/10" data-testid={`${testIdPrefix}-strip`}>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Reference images <span className="normal-case tracking-normal">({images.length}/{max}, used for next generate)</span></span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {images.map((url, i) => (
                    <div key={url} className="relative w-11 h-11 rounded-md overflow-hidden border border-white/15 group/ref" data-testid={`${testIdPrefix}-item-${i}`}>
                        <img src={url} alt={`Reference ${i + 1}`} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer" />
                        <button type="button" onClick={() => onChange(images.filter(u => u !== url))} disabled={disabled}
                            className="absolute top-0 right-0 w-4 h-4 bg-black/70 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover/ref:opacity-100 transition-opacity"
                            data-testid={`${testIdPrefix}-remove-${i}`}><X className="w-3 h-3" /></button>
                    </div>
                ))}
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid={`${testIdPrefix}-file-input`} />
                <button type="button" className={btn} disabled={disabled || full || uploading} onClick={() => fileRef.current?.click()} data-testid={`${testIdPrefix}-upload`}>
                    {uploading ? <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" /> : <Upload className="w-3 h-3" />} Upload
                </button>
                <button type="button" className={btn} disabled={disabled || full} onClick={() => setPicker('assets')} data-testid={`${testIdPrefix}-user-assets`}>
                    <FolderOpen className="w-3 h-3" /> User assets
                </button>
                <button type="button" className={btn} disabled={disabled || full} onClick={() => setPicker('library')} data-testid={`${testIdPrefix}-library`}>
                    <Users className="w-3 h-3" /> Library
                </button>
            </div>
            {picker === 'assets' && <UserAssetsModal activeTab="all" onSelect={(url) => pick(url)} onClose={() => setPicker(null)} />}
            {picker === 'library' && <ActorsLibraryModal onSelect={(a) => pick(a?.imageUrl)} onClose={() => setPicker(null)} />}
        </div>
    );
};
